import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { TeamFlag } from "@/components/TeamFlag";
import { toast } from "sonner";
import { Lock, Save, Sparkles } from "lucide-react";
import type { MatchData } from "@/components/MatchCard";

export interface SpecialBetRow {
  id: string;
  tournament_id: string;
  title_cs: string;
  title_en: string;
  description_cs: string | null;
  description_en: string | null;
  answer_type: "team" | "number";
  category: string;
  default_points: number;
  lock_on_first_match: boolean;
  lock_at: string | null;
  sort_order: number;
  correct_answer_text: string | null;
  correct_answer_number: number | null;
  is_published: boolean;
}

interface BetPrediction {
  id: string;
  bet_id: string;
  answer_text: string | null;
  answer_number: number | null;
  points_awarded: number;
}

interface LeagueBetSetting {
  bet_id: string;
  enabled: boolean;
  points_override: number | null;
}

interface Props {
  leagueId: string;
  tournamentId: string | null;
  userId: string;
  matches: MatchData[];
}

/** Localized title/description helper. */
export const betTitle = (b: SpecialBetRow, lang: string) =>
  lang.startsWith("cs") ? b.title_cs : b.title_en || b.title_cs;
export const betDescription = (b: SpecialBetRow, lang: string) =>
  (lang.startsWith("cs") ? b.description_cs : b.description_en) || null;

/** Effective lock timestamp (ms) for a bet, mirroring the DB function. */
export const betLockAtMs = (b: SpecialBetRow, matches: MatchData[]): number | null => {
  const explicit = b.lock_at ? new Date(b.lock_at).getTime() : null;
  if (!b.lock_on_first_match) return explicit;
  const firstKickoff = matches.reduce<number | null>((min, m) => {
    const t = new Date(m.kickoff_at).getTime();
    return min === null || t < min ? t : min;
  }, null);
  if (explicit === null) return firstKickoff;
  if (firstKickoff === null) return explicit;
  return Math.min(explicit, firstKickoff);
};

export const SpecialBetsTab = ({ leagueId, tournamentId, userId, matches }: Props) => {
  const { t, i18n } = useTranslation();
  const [bets, setBets] = useState<SpecialBetRow[]>([]);
  const [settings, setSettings] = useState<Record<string, LeagueBetSetting>>({});
  const [preds, setPreds] = useState<Record<string, BetPrediction>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const teams = useMemo(() => {
    const s = new Set<string>();
    matches.forEach((m) => { s.add(m.home_team); s.add(m.away_team); });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [matches]);

  const load = async () => {
    if (!tournamentId) { setLoading(false); return; }
    setLoading(true);
    const [{ data: b }, { data: ls }, { data: p }] = await Promise.all([
      supabase.rpc("get_special_bets", { _tournament_id: tournamentId }),
      supabase.from("league_special_bets").select("bet_id,enabled,points_override").eq("league_id", leagueId),
      supabase.from("special_bet_predictions").select("id,bet_id,answer_text,answer_number,points_awarded").eq("league_id", leagueId).eq("user_id", userId),
    ]);
    setBets(((b as SpecialBetRow[]) ?? []).filter((x) => x.is_published));
    const sMap: Record<string, LeagueBetSetting> = {};
    (ls ?? []).forEach((x: any) => { sMap[x.bet_id] = x; });
    setSettings(sMap);
    const pMap: Record<string, BetPrediction> = {};
    (p ?? []).forEach((x: any) => { pMap[x.bet_id] = x; });
    setPreds(pMap);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leagueId, tournamentId, userId]);

  const visibleBets = useMemo(
    () => bets.filter((b) => settings[b.id]?.enabled !== false),
    [bets, settings],
  );

  const pointsFor = (b: SpecialBetRow) => settings[b.id]?.points_override ?? b.default_points;

  const formatCountdown = (ms: number) => {
    if (ms <= 0) return "0" + t("groups.seconds");
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const parts: string[] = [];
    if (d) parts.push(`${d}${t("groups.days")}`);
    if (d || h) parts.push(`${h}${t("groups.hours")}`);
    if (d || h || m) parts.push(`${m}${t("groups.minutes")}`);
    parts.push(`${sec}${t("groups.seconds")}`);
    return parts.join(" ");
  };

  const save = async (b: SpecialBetRow) => {
    const existing = preds[b.id];
    const raw = drafts[b.id] ?? (b.answer_type === "team"
      ? existing?.answer_text ?? ""
      : existing?.answer_number != null ? String(existing.answer_number) : "");
    if (raw === "") { toast.error(t("specialBets.pickAnswer")); return; }
    setSaving(b.id);
    const payload = {
      bet_id: b.id,
      league_id: leagueId,
      user_id: userId,
      answer_text: b.answer_type === "team" ? raw : null,
      answer_number: b.answer_type === "number" ? Math.max(0, parseInt(raw) || 0) : null,
    };
    const { data, error } = await supabase
      .from("special_bet_predictions")
      .upsert(payload, { onConflict: "bet_id,league_id,user_id" })
      .select()
      .maybeSingle();
    setSaving(null);
    if (error) {
      toast.error(error.hint === "special_bet_locked" ? t("specialBets.locked") : error.message);
      return;
    }
    toast.success(t("groups.saved"));
    if (data) setPreds((prev) => ({ ...prev, [b.id]: data as BetPrediction }));
  };

  if (!tournamentId) return <p className="text-sm text-muted-foreground">{t("groups.noTournament")}</p>;
  if (loading) return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;
  if (visibleBets.length === 0) return <p className="text-sm text-muted-foreground">{t("specialBets.none")}</p>;

  const categories = Array.from(new Set(visibleBets.map((b) => b.category)));

  return (
    <div className="space-y-4">
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="p-4 text-sm flex items-start gap-2">
          <Sparkles className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
          <div><strong>{t("specialBets.title")}:</strong> {t("specialBets.hint")}</div>
        </CardContent>
      </Card>

      {categories.map((cat) => (
        <div key={cat} className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t(`specialBets.category.${cat}`, cat)}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {visibleBets.filter((b) => b.category === cat).map((b) => {
              const existing = preds[b.id];
              const lockAt = betLockAtMs(b, matches);
              const locked = lockAt !== null && lockAt <= now;
              const msToLock = lockAt !== null ? lockAt - now : Infinity;
              const value = drafts[b.id] ?? (b.answer_type === "team"
                ? existing?.answer_text ?? ""
                : existing?.answer_number != null ? String(existing.answer_number) : "");
              const correct = b.answer_type === "team" ? b.correct_answer_text : (b.correct_answer_number != null ? String(b.correct_answer_number) : null);
              const desc = betDescription(b, i18n.language);
              return (
                <Card key={b.id} className="rounded-2xl border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-start justify-between gap-2 text-base">
                      <span className="min-w-0">{betTitle(b, i18n.language)}</span>
                      <div className="flex shrink-0 items-center gap-2">
                        {locked && (
                          <Badge variant="destructive" className="rounded-full text-[10px] gap-1">
                            <Lock className="h-3 w-3" />{t("groups.lockedShort")}
                          </Badge>
                        )}
                        <Badge variant="secondary" className="rounded-full text-[10px]">
                          {existing && correct ? `+${existing.points_awarded}` : `${pointsFor(b)}`} {t("common.points")}
                        </Badge>
                      </div>
                    </CardTitle>
                    {desc && <p className="text-xs text-muted-foreground">{desc}</p>}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {locked ? (
                      <div className="flex items-center gap-2 rounded-md border border-destructive/20 bg-destructive/10 px-3 py-2 text-xs font-medium text-destructive">
                        <Lock className="h-3.5 w-3.5" />
                        <span>{t("specialBets.locked")}</span>
                      </div>
                    ) : lockAt !== null ? (
                      <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                        {t("groups.locksIn")}: <span className="tabular-nums font-semibold">{formatCountdown(msToLock)}</span>
                      </div>
                    ) : null}

                    {b.answer_type === "team" ? (
                      <Select value={value} onValueChange={(v) => setDrafts((d) => ({ ...d, [b.id]: v }))} disabled={locked}>
                        <SelectTrigger className="h-10"><SelectValue placeholder={t("groups.pickTeam")} /></SelectTrigger>
                        <SelectContent>
                          {teams.map((tm) => (
                            <SelectItem key={tm} value={tm}>
                              <span className="inline-flex items-center gap-2"><TeamFlag team={tm} size="sm" />{tm}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        value={value}
                        disabled={locked}
                        onChange={(e) => setDrafts((d) => ({ ...d, [b.id]: e.target.value }))}
                        className="h-10 rounded-xl"
                        placeholder={t("specialBets.numberPlaceholder")}
                      />
                    )}

                    {correct && (
                      <div className="rounded-xl bg-muted/40 px-3 py-2 text-xs space-y-1">
                        <div className="font-semibold text-muted-foreground">{t("groups.officialResult")}</div>
                        <div><b>{correct}</b></div>
                      </div>
                    )}

                    {!locked && (
                      <Button
                        onClick={() => save(b)}
                        disabled={saving === b.id}
                        className="w-full h-10 rounded-full bg-gradient-hero text-primary-foreground shadow-gold border-0"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {existing ? t("groups.update") : t("common.save")}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
